/// <amd-module name="Build/Designer/Services/PipelineEditorViewService" />
import { BuildDefinition, BuildRepository, BuildRequestValidationResult, YamlProcess } from "Build/Client/Contracts/Build";
import { CheckForHostedMacData, CheckForHostedMacDataRequestParams } from "Build/Client/Contracts/DataProvider";
import { SaveResult } from "Build/Common/Library/Designer.types";
import { RepositoryDescriptor } from "Build/Common/Library/Types";
import { WellKnownExceptions } from "Build/Designer/Constants";
import { getSourceProviderTile } from "Build/Designer/EditorAuthLandingPage";
import { addNavigateAwayListener, getLoseChangesDialog, removeNavigateAwayListener, showLoseChangesDialog } from "Build/Designer/LoseChangesDialog";
import * as Resources from "Build/Designer/Resources";
import { BannerContent, clearWarning, showWarning } from "Build/Designer/WarningBanner";
import { FileContentInfo, IYamlFileService } from "Build/Designer/YamlFileService";
import { ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { VssServerError } from "VSS/Platform/Fetch";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { getFileName } from "../Util/File";

const hostedMacWarningBannerId = "hosted-mac-warning";
const hostedMacLearnMoreLink = "https://go.microsoft.com/fwlink/?linkid=870426";
const pipelineDataProviderKey = "ms.vss-build-web.pipeline-editor-data-provider";

export interface PipelineEditorAuthenticationRequirements {
    isLoginRequired: boolean;
    shouldShowLandingPage: boolean;
    loginUrl?: string;
}

export interface PipelineEditorServiceConnection {
    appHelpLink: string;
    appName: string;
    autoUpgradeToAppConnection: boolean;
    connectionId: string;
    isAppConnection: boolean;
    login: string;
    nonce: string;
}

interface PipelineEditorData {
    definition: BuildDefinition;
    content: string;
    repository: BuildRepository;
    yamlFileUrl: string;
    branch: string;
    objectId: string;
    templatePath: string;
    fileErrorMessage: string;
    serviceConnection: PipelineEditorServiceConnection;
    authenticationRequirements: PipelineEditorAuthenticationRequirements;
}

export interface IPipelineEditorViewService extends IVssService {
    clearEditorError(): void;
    getAuthentication(): PipelineEditorAuthenticationRequirements;
    getBranch(): string;
    getConnection(): PipelineEditorServiceConnection;
    getDefinition(): BuildDefinition;
    getEditorError(): IReadonlyObservableValue<EditorError | undefined>;
    getIsYamlDirty(): IReadonlyObservableValue<boolean>;
    getSourceProvider(): ISourceProvider;
    getSpinnerMessage(): string;
    getYamlContent(): IReadonlyObservableValue<string>;
    getYamlFileUrl(): string;
    getYamlObjectId(): string;
    getYamlPath(): string;
    getIsTemplate(): boolean;
    getTemplatePath(): string | undefined;
    getDefinitionYamlEditorUrl(): string;
    getIsNewFile(): boolean;
    getRepository(): BuildRepository;
    handleRunBuildError(error?: Error): void;
    setYamlContent(content: string): void;
    shouldShowLandingPage(): boolean;
    switchBranch(branchName: string): void;
    updateSavedDefinition(savedDefinition: BuildDefinition): void;
    updateSavedYaml(result: SaveResult, content: string): void;
}

export interface EditorError {
    message: string;
    isDismissable?: boolean;
    proposeClassicDesigner?: boolean;
    proposeAuthorizeAndRun?: boolean;
}

class PipelineEditorViewService extends VssService implements IPipelineEditorViewService {
    private data: PipelineEditorData;
    private definition: BuildDefinition;
    private editorError = new ObservableValue<EditorError | undefined>(undefined);
    private historyService: IVssHistoryService;
    private isYamlDirty = new ObservableValue(false);
    private spinnerMessage = "";
    private yamlContent = new ObservableValue("");
    private yamlFileInfo = new ObservableValue({} as FileContentInfo);

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<PipelineEditorData>(pipelineDataProviderKey)!;
        if (!data) {
            this.editorError.value = this.getDataProviderError(contributionService.getDataProviderExceptions()[pipelineDataProviderKey]);
            return;
        }

        const { repository } = data;

        // External source providers like GitHub require user authentication to view/edit the YAML.
        if (data.authenticationRequirements.isLoginRequired) {
            if (data.authenticationRequirements.shouldShowLandingPage) {
                this.editorError.value = undefined;
            } else {
                const sourceProviderTile = getSourceProviderTile(this.pageContext, repository.type.toLowerCase());
                const sourceProviderName = sourceProviderTile ? sourceProviderTile.name : "";
                this.spinnerMessage = sourceProviderName
                    ? format(Resources.AuthorizingWithProviderMessage, sourceProviderName)
                    : Resources.AuthorizingMessage;
            }

            if (!data.authenticationRequirements.loginUrl) {
                this.editorError.value = { message: Resources.LoginUrlNotSpecified };
            }
        }

        this.data = data;
        this.definition = data.definition;
        this.yamlContent.value = data.content || "";

        const yamlProcess = data.definition.process as YamlProcess;
        this.yamlFileInfo.value = {
            path: this.data.templatePath ? this.data.templatePath : yamlProcess.yamlFilename,
            branch: data.branch || (repository.defaultBranch || "").replace("refs/heads/", ""),
            content: this.yamlContent.value,
            objectId: this.data.objectId,
            url: this.data.yamlFileUrl
        };

        if (this.data.templatePath && this.data.fileErrorMessage && !yamlProcess.yamlFilename) {
            let message = this.data.fileErrorMessage;

            if (equals(repository.type, RepositoryTypes.TfsGit, true) && repository.id && repository.name) {
                message = message.replace(repository.id, repository.name);
            }
            this.editorError.value = { message, isDismissable: true };
        }

        if (this.getBranch() && this.getBranch() !== this.historyService.getState().branch) {
            this.updateUrl();
        }

        const navigationService = pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
        navigationService.setPageTitle(this.getPageTitle());

        addNavigateAwayListener(this.onNavigateAway);

        this.checkForHostedMac();
    }

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);

        removeNavigateAwayListener(this.onNavigateAway);
    };

    public clearEditorError() {
        this.editorError.value = undefined;
    }

    public getAuthentication() {
        return !!this.data && this.data.authenticationRequirements;
    }

    public getBranch() {
        return this.yamlFileInfo.value.branch;
    }

    public getConnection() {
        return this.data.serviceConnection || {};
    }

    public getDefinition() {
        return this.definition;
    }

    public getEditorError() {
        return this.editorError;
    }

    public getIsYamlDirty() {
        return this.isYamlDirty;
    }

    public getRepository() {
        return this.data.repository;
    }

    public getSourceProvider() {
        const sourceProviderService = this.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        return this.data.repository && sourceProviderService.getSourceProvider(this.data.repository.type);
    }

    public getSpinnerMessage() {
        return this.spinnerMessage;
    }

    public getYamlContent() {
        return this.yamlContent;
    }

    public getYamlFileUrl() {
        return this.yamlFileInfo.value.url;
    }

    public getIsTemplate(): boolean {
        return !!this.data && !!this.data.templatePath;
    }

    public getTemplatePath() {
        return this.data.templatePath;
    }

    public getDefinitionYamlEditorUrl() {
        const tfsPageData = getTFSPageData(this.pageContext)!;
        return routeUrl(this.pageContext, "ms.vss-build-web.ci-definition-designer-route", {
            project: tfsPageData.project!.name,
            pipelineId: this.getDefinition().id.toString(),
            branch: this.getBranch()
        });
    }

    public getYamlPath() {
        return this.yamlFileInfo.value.path;
    }

    public getYamlObjectId() {
        return this.yamlFileInfo.value.objectId;
    }

    public getIsNewFile() {
        return !!this.data.templatePath && !!this.data.fileErrorMessage;
    }

    public handleRunBuildError(error?: Error) {
        let message = (error && error.message) || Resources.RunBuildErrorMessage;

        // Attempt to show more error detail if it was returned as BuildRequestValidationResults.
        const vssError = error as VssServerError;
        if (vssError && vssError.serverError && vssError.serverError.customProperties) {
            const validationResults: BuildRequestValidationResult[] = vssError.serverError.customProperties.ValidationResults;
            if (validationResults && validationResults.length > 0) {
                message += "\n" + validationResults.map(result => result.message).join("\n");

                // Show the Authorize Resources action if that appears to be the reason for the failure
                const yamlAuthorizationErrorUrl = "https://aka.ms/yamlauthz";
                if (message.indexOf(yamlAuthorizationErrorUrl) > 0) {
                    this.editorError.value = {
                        message,
                        proposeAuthorizeAndRun: true
                    };

                    return;
                }
            }
        }

        this.editorError.value = { message };
    }

    public setYamlContent(content: string) {
        if (this.yamlContent.value !== content) {
            this.yamlContent.value = content;

            const isDirty = content !== this.yamlFileInfo.value.content;
            if (isDirty !== this.isYamlDirty.value) {
                this.isYamlDirty.value = isDirty;
            }
        }
    }

    public shouldShowLandingPage(): boolean {
        const authentication = this.getAuthentication();
        return authentication.isLoginRequired && authentication.shouldShowLandingPage;
    }

    public switchBranch(branchName: string): Promise<void> | undefined {
        if (branchName !== this.getBranch()) {
            if (this.isYamlDirty.value) {
                this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout(onDismiss => {
                    const leave = () => {
                        onDismiss();
                        this.loadYaml(branchName);
                    };
                    const stay = onDismiss;
                    return getLoseChangesDialog(leave, stay);
                });
            } else {
                return this.loadYaml(branchName);
            }
        }
    }

    public updateSavedDefinition(savedDefinition: BuildDefinition) {
        this.definition = savedDefinition;
    }

    public updateSavedYaml(result: SaveResult, content: string) {
        this.isYamlDirty.value = false;
        this.yamlFileInfo.value = {
            ...this.yamlFileInfo.value,
            content,
            branch: result.branch,
            objectId: result.objectId,
            url: result.url
        };

        this.updateUrl();
        this.checkForHostedMac();
    }

    private getPageTitle() {
        return (this.getIsTemplate() && getFileName(this.data.templatePath)) || this.definition?.name || Resources.EditorDefaultTitle;
    }

    private loadYaml(branchName: string) {
        const fileName = this.getYamlPath();
        const buildRepo = this.data.repository;
        const repository: RepositoryDescriptor = {
            connectionId: buildRepo.properties && buildRepo.properties["connectedServiceId"],
            id: buildRepo.id,
            name: buildRepo.name,
            sourceProviderType: buildRepo.type
        };

        this.yamlContent.value = "";
        this.yamlFileInfo.value = {} as FileContentInfo;

        this.editorError.value = undefined;

        const historyService = this.historyService;
        const url: string = historyService.generateUrl({ branch: branchName });
        historyService.pushState(undefined, url);

        return this.pageContext
            .getService<IYamlFileService>("IYamlFileService")
            .getFileContentData(repository, branchName, fileName)
            .then(
                (fileInfo: FileContentInfo) => {
                    this.updateFileInfo(fileInfo);
                    this.yamlContent.value = fileInfo.content;
                },
                (error: Error) => {
                    this.editorError.value = {
                        message: error.message,
                        isDismissable: true
                    };
                    this.updateFileInfo({
                        content: "",
                        branch: branchName,
                        path: fileName,
                        objectId: "",
                        url: ""
                    });
                }
            );
    }

    private updateFileInfo(fileInfo: FileContentInfo) {
        this.yamlFileInfo.value = fileInfo;

        this.checkForHostedMac();
    }

    private checkForHostedMac() {
        const CheckForHostedMacDataProviderKey = "ms.vss-build-web.check-for-hosted-mac-data-provider";
        const requestParams: CheckForHostedMacDataRequestParams = {
            pipelineId: this.getDefinition().id,
            sourceBranch: this.getBranch()
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getDataAsync<CheckForHostedMacData>(CheckForHostedMacDataProviderKey, requestParams, true).then(usingHostedMacData => {
            if (usingHostedMacData) {
                if (usingHostedMacData.usingHostedMac) {
                    const globalMessageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                    const currentBanner = globalMessageService.getBanner();
                    if (!currentBanner || currentBanner.settingId !== hostedMacWarningBannerId) {
                        const bannerContent: BannerContent = {
                            id: hostedMacWarningBannerId,
                            links: [
                                {
                                    href: hostedMacLearnMoreLink,
                                    name: Resources.LearnMore,
                                    supportsFPS: false
                                }
                            ],
                            messageFormat: Resources.HostedMacWarning
                        };

                        showWarning(this.pageContext, bannerContent);
                    }
                } else {
                    clearWarning(this.pageContext, hostedMacWarningBannerId);
                }
            }
        });
    }

    private getDataProviderError(exception: IServerException): EditorError {
        // Help the user navigate to the classic pipeline editor for fixable issues (ex: service connection)
        if (exception && exception.exceptionType) {
            switch (exception.exceptionType) {
                case WellKnownExceptions.ServiceEndpointNotFoundException:
                    return {
                        message: format(Resources.EditorErrorServiceConnection, exception.message),
                        proposeClassicDesigner: true
                    };

                case WellKnownExceptions.GitRepositoryNotFoundException:
                    return {
                        message: format(Resources.EditorErrorGitRepoNotFound, exception.message),
                        proposeClassicDesigner: true
                    };

                case WellKnownExceptions.DefinitionTypeNotSupportedException:
                    return {
                        message: format(Resources.EditorErrorNotYamlPipeline, exception.message),
                        proposeClassicDesigner: true
                    };
            }
        }

        return {
            message: format(Resources.EditorErrorDefaultPipelineFetch, (exception && exception.message) || "")
        };
    }

    private onNavigateAway = (ev: BeforeUnloadEvent | CustomEvent) => {
        if (this.isYamlDirty.value) {
            showLoseChangesDialog(this.pageContext, ev);
        }
    };

    private updateUrl() {
        const url = this.historyService.generateUrl({ branch: this.getBranch() });
        this.historyService.replaceState(undefined, url);
    }
}

const serviceName = "IPipelineEditorViewService";

export function getPipelineEditorViewService(pageContext: IVssPageContext): IPipelineEditorViewService {
    return pageContext.getService<IPipelineEditorViewService>(serviceName);
}

Services.add(serviceName, { serviceFactory: PipelineEditorViewService });
